import {
  ChangeEventHandler,
  forwardRef,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  RefObject,
} from 'react';
import cn from 'classnames';

import { BaseProps } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';

import { FieldSelectView as FieldView } from '../styles';

type FieldSelectViewProps = {
  labelId?: string;
  listboxId?: string;
  activeId?: string;
  hasError?: boolean;
  errorTextId?: string;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onFieldClick?: () => void;
  onKeyDownReset?: KeyboardEventHandler<HTMLImageElement>;
  onClickReset?: MouseEventHandler<HTMLImageElement>;
  onSearch?: ChangeEventHandler<HTMLInputElement>;
  label?: string;
  selectedView: ReactNode[] | string;
  iconAfter?: ReactNode;
  status?: string;
  open?: boolean;
  disabled?: boolean;
  required?: boolean;
  withSearch?: boolean;
  searchViewRef?: RefObject<HTMLInputElement>;
  inputValue?: string | number;
} & BaseProps;

export const FieldSelectView = forwardRef<HTMLDivElement, FieldSelectViewProps>(
  (
    {
      labelId,
      listboxId,
      activeId,
      hasError,
      errorTextId,
      onKeyDown,
      onFieldClick,
      onKeyDownReset,
      onClickReset,
      onSearch,
      label,
      selectedView,
      iconAfter,
      className,
      status,
      open,
      disabled,
      required,
      withSearch,
      searchViewRef,
      inputValue,
    },
    ref,
  ) => {
    const isMultiple = Array.isArray(selectedView);
    const isViewLabelUp = isMultiple ? selectedView.length > 0 : selectedView;
    const selected = withSearch ? open || inputValue || isViewLabelUp : isViewLabelUp;

    const selectedViewBox = isMultiple ? (
      <div className="selected-chips">
        {selectedView}
        {withSearch && (
          <input
            className="input-transparent"
            ref={searchViewRef}
            type="text"
            value={inputValue}
            onChange={onSearch}
          />
        )}
      </div>
    ) : (
      <div className="selected-label">
        {withSearch ? (
          <input
            className="input-transparent"
            ref={searchViewRef}
            type="text"
            value={inputValue}
            onChange={onSearch}
          />
        ) : (
          selectedView
        )}
      </div>
    );

    return (
      <FieldView
        ref={ref}
        role="combobox"
        aria-haspopup="listbox"
        aria-labelledby={labelId}
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={open ? activeId : undefined}
        aria-invalid={hasError}
        aria-describedby={errorTextId}
        className={cn(className, status, { open, selected, required, disabled })}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={onKeyDown}
        onClick={onFieldClick}
      >
        <div className="labels">
          <label id={labelId} className="label">
            {label}
          </label>
          {selectedViewBox}
        </div>
        <div className="icons">
          {Boolean(selectedView?.length) && (
            <Icon
              tabIndex={disabled ? -1 : 0}
              onKeyDown={onKeyDownReset}
              onClick={onClickReset}
              name="x"
              aria-label="Clear selection"
              size="s20"
              role="button"
            />
          )}
          {Boolean(iconAfter) && iconAfter}
          <Icon className="caret-down" name="caret-down" size="s20" />
        </div>
      </FieldView>
    );
  },
);

FieldSelectView.displayName = 'FieldSelectView';
